// 設備庫存（DEV-15，SPEC #vms-web-inventory、#provisioning-inventory-api）的對話框：認領（可貼多列、逐列結果）、
// 指派到家戶（選家戶 → 直屬節點一起送；step-up 由 SDK 跳 StepUpHost；冪等鍵在對話框開著時固定，重送不會重複指派）、取消指派。
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { idempotencyKey, isKinioError } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import { inventoryApi, type InventoryAssignResult, type InventoryDevice, type MyOrg } from '../../lib/planeApi';
import { errText } from '../settings/OrgSection';
import { homeTargets } from '../orgtree/homeTargets';
import { parseClaimLines } from './inventoryModel';
import { serialOrTail } from './InventoryPage';

type ClaimRow = { serial: string; state: 'pending' | 'ok' | 'already' | 'not_found' | 'rate_limited' | 'error'; model?: string; code?: string };

export function ClaimDialog({ org, open, close, done }: { org: MyOrg; open: boolean; close(): void; done(): void }) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [rows, setRows] = useState<ClaimRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const parsed = parseClaimLines(text);

  async function run() {
    setBusy(true);
    const out: ClaimRow[] = parsed.lines.map((l) => ({ serial: l.serial, state: 'pending' }));
    setRows([...out]);
    let limited = false;
    for (let i = 0; i < parsed.lines.length; i++) {
      const l = parsed.lines[i];
      if (limited) {
        out[i] = { serial: l.serial, state: 'rate_limited' };
        setRows([...out]);
        continue;
      }
      try {
        const r = await inventoryApi.claim(org, l.serial, l.orderRef);
        out[i] = { serial: l.serial, state: r.already_in_inventory ? 'already' : 'ok', model: r.model };
      } catch (e) {
        const code = isKinioError(e) ? e.code : 'default';
        if (code === 'RATE_LIMITED') limited = true; // 之後的列不再送（防猜上限，整點後再試）
        out[i] = { serial: l.serial, state: code === 'CLAIM_NOT_FOUND' ? 'not_found' : code === 'RATE_LIMITED' ? 'rate_limited' : 'error', code };
      }
      setRows([...out]);
    }
    setBusy(false);
    done();
  }

  function reset() {
    setText('');
    setRows(null);
    close();
  }

  return (
    <Dialog open={open} title={t('inv.claim')} onClose={reset} wide>
      <div className="form" data-testid="inv-claim-dialog">
        <p className="muted small">{t('inv.claim_help')}</p>
        <label className="field">
          <span>{t('inv.claim_lines')}</span>
          <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('inv.claim_placeholder')} disabled={busy || !!rows} className="input mono" />
        </label>
        {parsed.bad.length > 0 && <p className="error small">{t('inv.claim_bad_lines', { lines: parsed.bad.join(', ') })}</p>}
        {parsed.over && <p className="error small">{t('inv.claim_over', { n: 50 })}</p>}
        {rows && (
          <div className="table-wrap" style={{ maxHeight: 280, overflow: 'auto' }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('inv.col_serial')}</th>
                  <th>{t('batch.col_result')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} data-claim-row={r.serial} data-state={r.state}>
                    <td className="mono">{r.serial}</td>
                    <td className={r.state === 'ok' || r.state === 'already' || r.state === 'pending' ? '' : 'error'}>
                      {t(`inv.claim_st_${r.state}`, { model: r.model ?? '', code: r.code ?? '' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="dialog-actions">
          {rows ? (
            <button className="btn btn-primary" disabled={busy} onClick={reset}>
              {t('common.close')}
            </button>
          ) : (
            <>
              <button className="btn" onClick={reset}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" disabled={busy || parsed.lines.length === 0 || parsed.bad.length > 0} onClick={() => void run()} data-testid="inv-claim-run">
                {t('inv.claim_run', { n: parsed.lines.length })}
              </button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export function AssignDialog({ org, devices, open, close, done }: { org: MyOrg; devices: InventoryDevice[]; open: boolean; close(): void; done(): void }) {
  const { t } = useTranslation();
  const targets = useQuery({ queryKey: ['ot', org.org_id, 'pair-targets'], queryFn: () => homeTargets(org), enabled: open, retry: false });
  const [filter, setFilter] = useState('');
  const [home, setHome] = useState('');
  const [key, setKey] = useState(() => idempotencyKey());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [results, setResults] = useState<InventoryAssignResult[] | null>(null);
  const shown = useMemo(() => (targets.data ?? []).filter((x) => x.node_id && (!filter || x.label.toLowerCase().includes(filter.toLowerCase()))), [targets.data, filter]);
  const picked = (targets.data ?? []).find((x) => x.home_id === home);
  const byRef = new Map(devices.map((d) => [d.device_ref, d]));

  async function run() {
    if (!picked?.node_id) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await inventoryApi.assign(org, { home_id: picked.home_id, node_id: picked.node_id, device_refs: devices.map((d) => d.device_ref) }, key);
      setResults(r.results);
      done();
    } catch (e) {
      setErr(e); // 同一把冪等鍵重試（網路斷掉重按不會重複指派）
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setFilter('');
    setHome('');
    setResults(null);
    setErr(null);
    setKey(idempotencyKey());
    close();
  }

  return (
    <Dialog open={open} title={t('inv.assign_title', { n: devices.length })} onClose={reset} wide>
      <div className="form" data-testid="inv-assign-dialog">
        {!results && (
          <>
            <p className="muted small">{t('inv.assign_help')}</p>
            {targets.isLoading && <p className="muted small">{t('common.loading')}</p>}
            {targets.isError && <p className="error">{errText(t, targets.error)}</p>}
            {targets.data && (
              <>
                <label className="field">
                  <span>{t('inv.assign_search')}</span>
                  <input className="input" type="search" value={filter} onChange={(e) => setFilter(e.target.value)} />
                </label>
                <label className="field">
                  <span>{t('inv.assign_home')}</span>
                  <select className="input" aria-label={t('inv.assign_home')} value={home} onChange={(e) => setHome(e.target.value)} size={Math.min(8, Math.max(2, shown.length))}>
                    {shown.map((x) => (
                      <option key={x.home_id} value={x.home_id}>
                        {x.label}
                        {x.unassigned ? ` (${t('ot.unassigned')})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                {shown.length === 0 && <p className="muted small">{t('inv.assign_no_home')}</p>}
              </>
            )}
          </>
        )}
        {err != null && <p className="error">{errText(t, err)}</p>}
        <div className="table-wrap" style={{ maxHeight: 280, overflow: 'auto' }}>
          <table className="grid">
            <thead>
              <tr>
                <th>{t('devices.col_model')}</th>
                <th>{t('inv.col_serial')}</th>
                <th>{t('batch.col_result')}</th>
              </tr>
            </thead>
            <tbody>
              {(results ?? devices.map((d) => ({ device_ref: d.device_ref, status: '' }) as InventoryAssignResult)).map((r) => {
                const d = byRef.get(r.device_ref);
                return (
                  <tr key={r.device_ref} data-assign-row={d ? serialOrTail(d) : r.device_ref.slice(-6)} data-state={r.status || 'idle'}>
                    <td>{d?.model ?? '—'}</td>
                    <td className="mono">{d ? serialOrTail(d) : '…' + r.device_ref.slice(-6)}</td>
                    <td className={r.status === 'failed' ? 'error' : ''}>
                      {!r.status
                        ? '—'
                        : r.status === 'failed'
                          ? t(`inv.err_${r.error?.error ?? 'default'}`, { defaultValue: t('org.err_default', { code: r.error?.error ?? '' }) })
                          : t(`inv.assign_st_${r.status}`, { defaultValue: t('common.state_unknown') })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="dialog-actions">
          {results ? (
            <button className="btn btn-primary" onClick={reset}>
              {t('common.close')}
            </button>
          ) : (
            <>
              <button className="btn" onClick={reset}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" disabled={busy || !picked?.node_id} onClick={() => void run()} data-testid="inv-assign-run">
                {t('inv.assign_run')}
              </button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export function UnassignDialog({ org, devices, open, close, done }: { org: MyOrg; devices: InventoryDevice[]; open: boolean; close(): void; done(): void }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  async function run() {
    setBusy(true);
    setErr(null);
    try {
      for (const d of devices) await inventoryApi.unassign(org, d.device_ref); // 冪等，失敗重按安全
      done();
      close();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} title={t('inv.unassign')} onClose={close}>
      <div className="form" data-testid="inv-unassign-dialog">
        <p>{t('inv.unassign_confirm', { n: devices.length })}</p>
        {err != null && <p className="error">{errText(t, err)}</p>}
        <div className="dialog-actions">
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-danger" disabled={busy} onClick={() => void run()} data-testid="inv-unassign-run">
            {t('inv.unassign')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
